// Story 1.30 — admin Bulletin queue list at /admin/bulletin.
// Wrapped by <AdminGuard> in App.tsx.

import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  DEFAULT_BULLETIN_FILTERS,
  type BulletinListFilters,
  type BulletinStatus,
  useAdminBulletinList,
} from "../features/admin/useAdminBulletinList";

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function filtersFromSearchParams(
  params: URLSearchParams
): BulletinListFilters {
  const status =
    (params.get("status") as BulletinStatus | null) ??
    DEFAULT_BULLETIN_FILTERS.status;
  return {
    status,
    title_search: params.get("title_search") ?? undefined,
    page: Number(params.get("page") ?? "1") || 1,
    page_size:
      Number(params.get("page_size") ?? String(DEFAULT_BULLETIN_FILTERS.page_size)) ||
      DEFAULT_BULLETIN_FILTERS.page_size,
  };
}

export function AdminBulletinListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );
  const query = useAdminBulletinList(filters);

  function updateFilter<K extends keyof BulletinListFilters>(
    key: K,
    value: BulletinListFilters[K] | undefined
  ) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "" || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, page)));
    setSearchParams(next);
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">Bulletin posts</h1>
            <p className="opacity-70 text-sm">
              Greenhold Bulletin — admin authoring. Public reader at{" "}
              <code className="font-mono">/bulletin</code> ships in Story 2.9.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/bulletin/new")}
          >
            New post
          </Button>
        </header>

        <Card>
          <CardContent className="flex flex-wrap gap-3 items-end p-4">
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Status</span>
              <select
                className="border rounded px-2 py-1 bg-white"
                value={filters.status}
                onChange={(e) =>
                  updateFilter("status", e.target.value as BulletinStatus)
                }
              >
                <option value="any">Any</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Title search</span>
              <input
                type="text"
                className="border rounded px-2 py-1 bg-white"
                placeholder="e.g. streamer"
                value={filters.title_search ?? ""}
                onChange={(e) =>
                  updateFilter("title_search", e.target.value)
                }
              />
            </label>
          </CardContent>
        </Card>

        {query.isPending ? (
          <Card>
            <CardContent className="p-8 text-center opacity-70">
              Loading…
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-destructive">
                {query.error.message ?? "Failed to load bulletin posts."}
              </p>
              <Button onClick={() => query.refetch()} variant="secondary">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : query.data.data.length === 0 ? (
          <EmptyState
            aria-label="No bulletin posts"
            description="No bulletin posts yet — write the first one."
            action={
              <Button
                variant="ghost"
                onClick={() => navigate("/admin/bulletin/new")}
              >
                New post
              </Button>
            }
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Showing {query.data.data.length} post
                {query.data.data.length === 1 ? "" : "s"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-brand-paper-100 text-left">
                  <tr>
                    <th className="px-4 py-2">Title</th>
                    <th className="px-4 py-2">Slug</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Published</th>
                    <th className="px-4 py-2">Updated</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-brand-paper-200 hover:bg-brand-paper-100"
                    >
                      <td className="px-4 py-2 font-medium">{row.title}</td>
                      <td className="px-4 py-2 opacity-80 font-mono text-xs">
                        {row.slug}
                      </td>
                      <td className="px-4 py-2">
                        {row.published_at ? (
                          <Badge variant="success">published</Badge>
                        ) : (
                          <Badge variant="warning">draft</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {row.published_at
                          ? formatRelative(row.published_at)
                          : "—"}
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {formatRelative(row.updated_at)}
                      </td>
                      <td className="px-4 py-2">
                        <Link
                          to={`/admin/bulletin/${row.id}`}
                          className="text-brand-forest-700 underline decoration-dotted hover:decoration-solid"
                        >
                          Edit →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {query.data && (query.data.has_next || filters.page > 1) ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page - 1)}
              disabled={filters.page <= 1}
            >
              ← Prev
            </Button>
            <span className="text-sm opacity-70 font-mono">
              Page {filters.page}
            </span>
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page + 1)}
              disabled={!query.data.has_next}
            >
              Next →
            </Button>
          </div>
        ) : null}
      </div>
    </main>
  );
}
